import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Col, Layout, message, Modal, Row } from 'antd';
import { startCase } from 'lodash';
import queryString from 'query-string';

import PdButton from 'components/pd-button';
import { TableList } from 'components/table-list';

import PromoSectionServices from 'services/promo-section';

import { errHandling } from 'utils';

import './style.scss';

const { Content } = Layout;
const { confirm } = Modal;

export function PromoSection() {
  const location = useLocation();
  const [currentPage] = useState(1);
  const [loadStatus, setLoadStatus] = useState(false);
  const [promoSectionList, setPromoSectionList] = useState([]);
  const navigate = useNavigate();
  const parsed = queryString.parse(location.search);

  const buttonAction = (row) => {
    const { id, status } = row;
    return status === 'active' ? (
      <Row className="button-table">
        <Col span={12} className="button">
          <PdButton
            className="height-32"
            type="danger"
            full
            onClick={handleDeactivate.bind(this, id)}
            outline>
            Deactive
          </PdButton>
        </Col>
        <Col span={12} className="button">
          <PdButton
            className="height-32"
            type="primary"
            full
            onClick={handleEdit.bind(this, id)}>
            Edit
          </PdButton>
        </Col>
      </Row>
    ) : (
      <Row className="button-table">
        <Col span={12} className="button">
          <PdButton
            className="height-32"
            type="primary"
            full
            onClick={handleReactivate.bind(this, id)}
            outline>
            Reactive
          </PdButton>
        </Col>
        <Col span={12} className="button">
          <PdButton
            className="height-32"
            type="danger"
            full
            onClick={handleDelete.bind(this, id)}>
            Delete
          </PdButton>
        </Col>
      </Row>
    );
  };

  const statusCell = ({ status }) => (
    <div className="status-indicator">
      <span className={status !== 'inactive' ? 'active' : 'hidden'}>
        {startCase(status)}
      </span>
    </div>
  );

  const columns = [
    {
      name: 'Title',
      selector: row => row.title,
    },
    {
      name: 'Status',
      cell: statusCell,
      center: true,
    },
    {
      name: '',
      cell: buttonAction,
      width: '25%',
    },
  ];

  async function handleReactivate(sectionId) {
    setLoadStatus(true);
    try {
      await PromoSectionServices.reactivatePromoSection(sectionId);
      fetchPromoSection();
      message.success('Promo section is reactivated');
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadStatus(false);
    }
  }

  function handleDeactivate(sectionId) {
    confirm({
      title: 'Are you sure to deactive this section?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      async onOk() {
        setLoadStatus(true);
        try {
          await PromoSectionServices.deactivatePromoSection(sectionId);
          fetchPromoSection();
          message.success('Promo section is deactivated');
        } catch (error) {
          errHandling(error);
        } finally {
          setLoadStatus(false);
        }
      },
    });
  }

  function handleDelete(sectionId) {
    confirm({
      title: 'Are you sure to delete this section?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      async onOk() {
        setLoadStatus(true);
        try {
          await PromoSectionServices.deletePromoSection(sectionId);
          fetchPromoSection();
          message.success('Promo section is deleted');
        } catch (error) {
          errHandling(error);
        } finally {
          setLoadStatus(false);
        }
      },
    });
  }

  function handleAdd() {
    navigate('/promo-section/create');
  }

  function handleEdit(id) {
    navigate({
      pathname: '/promo-section/edit',
      search: `?id=${id}`,
    });
  }

  function changePage(page) {
    if (page < 1) page = 1;
    parsed['page'] = page;
    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/stocks',
      search: stringified,
    });
  }

  async function fetchPromoSection() {
    setLoadStatus(true);
    try {
      const {
        data: { data = [] },
      } = await PromoSectionServices.getPromoSectionList();
      setPromoSectionList(data);
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadStatus(false);
    }
  }

  useEffect(() => {
    fetchPromoSection();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Layout className="main-container pd-cms-promo-section">
      <div className="content">
        <div className="pageTitle">Promo Section</div>
        <Content className="pageContent">
          <Row gutter={[18, 36]}>
            <Col lg={{ span: 4, offset: 19 }}>
              <PdButton
                className="height-32"
                type="primary"
                full
                onClick={handleAdd}>
                Add Promo Section
              </PdButton>
            </Col>
          </Row>

          <Row>
            {!loadStatus && (
              <TableList
                changePage={changePage}
                columns={columns}
                loadingOrder={loadStatus}
                orderList={promoSectionList}
                page={currentPage}
                responsive={false}
              />
            )}
          </Row>
        </Content>
      </div>
    </Layout>
  );
}

export default { PromoSection };
